import { X } from 'lucide-react';

type StateValue = string | number | boolean | null;
type StateMap = Record<string, StateValue>;

interface Props {
  value: unknown;
  onChange: (next: StateMap) => void;
}

type ValueType = 'text' | 'number' | 'boolean' | 'null';

function typeOf(v: StateValue): ValueType {
  if (v === null) return 'null';
  if (typeof v === 'boolean') return 'boolean';
  if (typeof v === 'number') return 'number';
  return 'text';
}

function defaultForType(type: ValueType): StateValue {
  if (type === 'null') return null;
  if (type === 'boolean') return false;
  if (type === 'number') return 0;
  return '';
}

/**
 * Trainer-facing replacement for the raw-JSON `Step.state` editor — the
 * free-form key/value blob shown in the live "state" panel while a step
 * plays (e.g. `{"i": 0, "comparisons": 3}`). A dictionary has no fixed
 * `properties` for JSON Forms to render controls for, which is why this was
 * previously a bare textarea; add/remove/rename rows here instead, keeping
 * insertion order by always rebuilding from the ordered `entries` list
 * rather than deleting and re-adding keys on an object literal.
 */
export function StateKeyValueEditor({ value, onChange }: Props) {
  const entries: Array<[string, StateValue]> = Object.entries(
    (value && typeof value === 'object' ? (value as StateMap) : {})
  );

  const emit = (nextEntries: Array<[string, StateValue]>) => {
    onChange(Object.fromEntries(nextEntries));
  };

  const setKey = (index: number, key: string) => {
    const next = [...entries];
    next[index] = [key, next[index][1]];
    emit(next);
  };

  const setValue = (index: number, v: StateValue) => {
    const next = [...entries];
    next[index] = [next[index][0], v];
    emit(next);
  };

  const setType = (index: number, type: ValueType) => {
    setValue(index, defaultForType(type));
  };

  const removeRow = (index: number) => {
    emit(entries.filter((_, i) => i !== index));
  };

  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="text-xs font-medium text-[#374151]">State</span>
        <button
          type="button"
          className="text-xs text-primary hover:underline"
          onClick={() => emit([...entries, ['', '']])}
        >
          + Add variable
        </button>
      </div>
      {entries.length === 0 && (
        <p className="text-[11px] text-[#9ca3af]">
          No variables shown yet — add one to display it in the state panel while this step plays.
        </p>
      )}
      {entries.map(([key, v], index) => {
        const type = typeOf(v);
        return (
          <div key={index} className="mb-1 flex items-center gap-1">
            <input
              value={key}
              onChange={(e) => setKey(index, e.target.value)}
              placeholder="name"
              className="w-24 rounded-md border border-[#d1d5db] px-2 py-1 text-sm"
            />
            <select
              value={type}
              onChange={(e) => setType(index, e.target.value as ValueType)}
              className="rounded-md border border-[#d1d5db] bg-white px-1 py-1 text-xs"
            >
              <option value="text">text</option>
              <option value="number">number</option>
              <option value="boolean">yes/no</option>
              <option value="null">empty</option>
            </select>
            {type === 'boolean' ? (
              <input
                type="checkbox"
                checked={!!v}
                onChange={(e) => setValue(index, e.target.checked)}
                className="h-4 w-4"
              />
            ) : type === 'number' ? (
              <input
                type="number"
                value={typeof v === 'number' ? v : 0}
                onChange={(e) => setValue(index, Number(e.target.value))}
                className="w-full rounded-md border border-[#d1d5db] px-2 py-1 text-sm"
              />
            ) : type === 'null' ? (
              <span className="flex-1 text-xs text-[#9ca3af]">—</span>
            ) : (
              <input
                type="text"
                value={typeof v === 'string' ? v : ''}
                onChange={(e) => setValue(index, e.target.value)}
                className="w-full rounded-md border border-[#d1d5db] px-2 py-1 text-sm"
              />
            )}
            <button
              type="button"
              aria-label={`Remove variable ${key || `#${index + 1}`}`}
              onClick={() => removeRow(index)}
              className="shrink-0 text-destructive"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
